import { getClosestNode } from '@/lib/tiptap-advanced-utils';
import { getBasicContentSchema, setNodeAttribute } from '@/lib/zy-tiptap-utils';
import type { CommandProps } from '@tiptap/core';
import { TableHeader } from '@tiptap/extension-table';
import { mergeAttributes } from '@tiptap/react';
import { createTableCellNodeView } from './table-cell-node-view';
import { parseImportedWidth } from './utils';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyTableHeader: {
            togglePrimaryKey: () => ReturnType;
            toggleForeignKey: () => ReturnType;
        };
    }
}

export const ZyTableHeaderHeadless = TableHeader.extend({
    allowGapCursor: false,

    content: function () {
        return getBasicContentSchema({
            requireAtLeastOneNode: true,
            additionalContentTypes: ['zydefnBlock', 'imageUpload'],
            restrictedContentTypes: ['zyInlineEnum'],
        });
    },

    addAttributes() {
        return {
            ...this.parent?.(),
            importedWidth: {
                default: null,
                parseHTML: (element: HTMLElement) => parseImportedWidth(element),
                renderHTML: attributes => {
                    // Use the importedWidth attribute if it is set unless the colwidth attribute is set
                    if (attributes.importedWidth == null || attributes.colwidth != null) {
                        return {};
                    }
                    return { width: `${attributes.importedWidth}%` };
                },
            },
            scope: {
                default: '',
                parseHTML: element => {
                    return element.getAttribute('scope') || '';
                },
                renderHTML: attributes => {
                    if (!attributes.scope) {
                        return {};
                    }
                    return { scope: attributes.scope };
                },
            },
            primaryKey: {
                default: false,
                parseHTML: element => {
                    const classAttr = element.getAttribute('class') || '';
                    return classAttr.includes('dbPrimaryKey') || classAttr.includes('dbprimarykey');
                },
                renderHTML: attributes => {
                    if (!attributes.primaryKey) {
                        return {};
                    }
                    return { class: 'dbPrimaryKey' };
                },
            },
            foreignKey: {
                default: false,
                parseHTML: element => {
                    const classAttr = element.getAttribute('class') || '';
                    return classAttr.includes('dbForeignKey') || classAttr.includes('dbforeignkey');
                },
                renderHTML: attributes => {
                    if (!attributes.foreignKey) {
                        return {};
                    }
                    return { class: 'dbForeignKey' };
                },
            },
        };
    },

    renderHTML({ HTMLAttributes }) {
        return ['th', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands() {
        return {
            ...this.parent?.(),
            togglePrimaryKey:
                () =>
                ({ dispatch, editor, state }: CommandProps) => {
                    if (dispatch) {
                        const tableHeaderNode = getClosestNode(editor, { nodeName: 'tableHeader' });
                        if (tableHeaderNode) {
                            // Toggle primary key
                            setNodeAttribute(state.tr, dispatch, tableHeaderNode.pos, 'primaryKey', !tableHeaderNode.node.attrs.primaryKey);
                            return true;
                        }
                    }
                    return false;
                },
            toggleForeignKey:
                () =>
                ({ dispatch, editor, state }: CommandProps) => {
                    if (dispatch) {
                        const tableHeaderNode = getClosestNode(editor, { nodeName: 'tableHeader' });
                        if (tableHeaderNode) {
                            // Toggle foreign key
                            setNodeAttribute(state.tr, dispatch, tableHeaderNode.pos, 'foreignKey', !tableHeaderNode.node.attrs.foreignKey);
                            return true;
                        }
                    }
                    return false;
                },
        };
    },
});

export const ZyTableHeader = ZyTableHeaderHeadless.extend({
    addNodeView() {
        return createTableCellNodeView('th');
    },
});
